import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { userFacingMessage } from '../api';
import { LoadErrorState, LoadingState } from '../core-components/theme/components/Screen/ScreenStates';
import type { StatePlacement } from '../core-components/theme/components/Screen/StateArea';

const statusSx = { display: 'flex', flexDirection: 'column', flex: '1 0 auto', minWidth: 0 } as const;

interface SessionCheckingProps {
  /** The device is offline, so the check waits for the connection. */
  readonly paused?: boolean;
  readonly placement?: StatePlacement;
}

/** The wait while the server says who is signed in — a status region, so assistive tech hears it and why it is waiting. */
export const SessionChecking = ({ paused = false, placement = 'window' }: SessionCheckingProps) => {
  const { t } = useTranslation();
  return (
    <Box role="status" sx={statusSx}>
      <LoadingState label={t('session.checking', 'Checking your sign-in')} paused={paused} placement={placement} />
    </Box>
  );
};

interface SessionCheckFailedProps {
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly placement?: StatePlacement;
}

/** The server could not be asked who is signed in: said plainly, with a retry — never mistaken for being signed out. */
export const SessionCheckFailed = ({ error, onRetry, placement = 'window' }: SessionCheckFailedProps) => {
  const { t } = useTranslation();
  return (
    <LoadErrorState
      title={t('session.checkFailed', 'Couldn’t check your sign-in')}
      reason={userFacingMessage(error)}
      onRetry={onRetry}
      placement={placement}
    />
  );
};
